import { useEffect, useState } from "react";
import { Card, Input, Button, Typography } from "@material-tailwind/react";
import { UserIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import { toast } from "react-toastify";
import VerifyCode from "./VerifyCode";
import { useAuth } from "../auth/useAuth.hook";
import { useNavigate } from "react-router-dom";
import { ValidationMessages, validateEmail } from "../utils/validationMessages";

export function SendCode() {
  const [email, setEmail] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [agentId, setAgentId] = useState(null);

  const apiUri = import.meta.env.VITE_API_URI as string;

  const getEmailErrorMessageFor400 = (error: any): string => {
    const responseMessage = error?.response?.data?.message;
    const normalizedMessage = Array.isArray(responseMessage)
      ? responseMessage.join(" ").toLowerCase()
      : String(responseMessage || "").toLowerCase();

    if (
      normalizedMessage.includes("invalid email") ||
      normalizedMessage.includes("email inválido") ||
      normalizedMessage.includes("must be an email")
    ) {
      return ValidationMessages.LOGIN.EMAIL_INVALID;
    }

    return ValidationMessages.LOGIN.EMAIL_NOT_FOUND;
  };

  useEffect(() => {
    if (accessToken) {
      navigate("/dashboard");
    }
  }, [accessToken, navigate]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    
    // Validar em tempo real
    if (value.trim()) {
      const validation = validateEmail(value);
      setEmailError(validation.message || null);
    } else {
      setEmailError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar antes de enviar
    const validation = validateEmail(email);
    if (!validation.isValid) {
      setEmailError(validation.message || null);
      toast.error(validation.message || ValidationMessages.GENERIC.REQUIRED_FIELD);
      return;
    }

    setIsLoading(true);
    try {
      const { status, data } = await axios.post(`${apiUri}login`, { email });
      if (status === 404 || status === 400) {
        const errorMsg = ValidationMessages.LOGIN.EMAIL_NOT_FOUND;
        setEmailError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      if (status === 201) {
        setAgentId(data.data.agentId);
        setIsCodeSent(true);
        setEmailError(null);
        toast.success("Código enviado com sucesso para seu e-mail");
      }
    } catch (err: any) {
      let errorMsg = ValidationMessages.LOGIN.SEND_CODE_ERROR;
      
      if (err.response?.status === 404) {
        errorMsg = ValidationMessages.LOGIN.EMAIL_NOT_FOUND;
        setEmailError(errorMsg);
      } else if (err.response?.status === 400) {
        // 400 can represent either invalid email format or user not found.
        errorMsg = getEmailErrorMessageFor400(err);
        setEmailError(errorMsg);
      } else if (!navigator.onLine) {
        errorMsg = ValidationMessages.GENERIC.NETWORK_ERROR;
      }
      
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex flex-col items-center justify-center w-full px-4 py-8 bg-gradient-to-br from-blue-500 to-purple-600">
        <Card className="w-full max-w-md p-8 rounded-xl shadow-lg bg-white flex flex-col items-center" placeholder="">
          <img
            src="./logo-preppo.png"
            alt="logo preppo"
            className="h-20 md:h-24 lg:h-28 w-auto mb-6"
          />
          {!isCodeSent ? (
            <>
              <Typography
                variant="h6"
                className="text-preppoBlue font-semibold tracking-tight text-center"
                placeholder=""
              >
                Entre na sua conta
              </Typography>
              <form
                className="mt-6 space-y-6 w-full"
                onSubmit={handleSubmit}
              >
                <div className="flex flex-col gap-3">
                  <Typography
                    variant="h6"
                    className="text-sm font-medium text-preppoBlue/90 text-center"
                    placeholder=""
                  >
                    Digite o e-mail cadastrado
                  </Typography>
                  <Input
                    size="lg"
                    placeholder="email@exemplo.com"
                    value={email}
                    icon={<UserIcon className="h-5 w-5 text-gray-500" />}
                    className={`focus:!ring-2 focus:!border-preppoBlue ${
                      emailError ? "!border-red-500 focus:!ring-red-500" : "focus:!ring-preppoBlue"
                    }`}
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    onChange={handleEmailChange}
                    crossOrigin=""
                  />
                  {emailError && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <Typography className="text-xs text-red-700 leading-relaxed" placeholder="">
                        {emailError}
                      </Typography>
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  className="mt-6 bg-blue-500 hover:bg-blue-600 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  fullWidth
                  placeholder=""
                  disabled={isLoading || !!emailError}
                >
                  {isLoading ? "Enviando..." : "Entrar"}
                </Button>
                <Typography
                  className="mt-6 text-center text-sm text-preppoBlue/80"
                  placeholder={undefined}
                >
                  Ainda não tem uma conta?{" "}
                  <a
                    href="/registration"
                    className="font-semibold text-blue-600 hover:underline"
                  >
                    Cadastre-se
                  </a>
                </Typography>
              </form>
            </>
          ) : (
            <VerifyCode agentId={agentId} />
          )}
          <Typography
            variant="small"
            className="text-center text-gray-500 mt-8"
            placeholder=""
          >
            &copy; {new Date().getFullYear()} Preppo. Todos os direitos reservados.
          </Typography>
        </Card>
      </div>
    </>
  );
}

export default SendCode;
